import { useAtom } from "@dbeining/react-atom";
import { Form as FormikForm, Formik } from "formik";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ParentEntityLifecycleBanner, isSiteScopedFormReadOnly } from "../../components/Layout/ParentEntityLifecycleBanner";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import type {
    ISiteOption,
    ITipperDetail,
    TipperCreatePayload,
    TipperUpdatePayload,
} from "../../customTypings/tipper/Tipper";
import { sitesService } from "../../services/sites/sites.service";
import { tippersService } from "../../services/tippers/tippers.service";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { useTipperRestoreOnCreateConflict } from "@/hooks/createConflict";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { filterDefaultsEligibleSites } from "@/features/wasteMovement/core/wasteMovementSiteSelection";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, emptyStringToNull, hydratePostalAddressForForm, mapPostalAddressToApi, nullToEmptyString, optionalNullableString, arePayloadsEqual, editFormBreadcrumbLabel, editPageTitle, EntityLabels, ListPageTitles, SectionLabels, getUserFacingErrorMessage, isUnchangedUpdateResponse, loadingPageTitle, logErrorForDev, NO_CHANGES_TOAST_MESSAGE, showToastError, showToastSuccess, showToastUnchanged, buildRecordLifecycleConfirmProps, appRoutes, filterEditFormLifecycleHeaderActions, getEditFormLifecycleProps } from "../../utils";
import {
    buildTipperAdminPagedListActions,
    canMutateTippers,
    formatTipperToastLabel,
    getTipperAdminActionSuccessMessage,
    type TipperAdminConfirmKind,
} from "../TippersListPage/tipperAdminListActions";
import { TipperEditFormGrid } from "./TipperEditFormGrid";

dayjs.extend(utc);

function formatTipperMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

function toUtcIsoDateOnly(value: string): string | null {
    const t = value.trim();
    if (!t) return null;
    return dayjs.utc(t, "YYYY-MM-DD").startOf("day").toISOString();
}

function fromUtcIsoToDateInput(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).format("YYYY-MM-DD");
}

type FormValues = TipperCreatePayload & { id?: string; licenceExpiryDate?: string };

const validationSchema = Yup.object({
    id: Yup.string().optional(),
    name: Yup.string().trim().required("Name is required.").max(255, "Name must be at most 255 characters."),
    addressLine1: optionalNullableString,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: optionalNullableString,
    carrierLicenceNumber: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() : v))
        .nullable()
        .transform((v) => v ?? "")
        .optional()
        .max(64, "Licence must be at most 64 characters."),
    licenceExpiryDate: optionalNullableString,
    isEnabled: Yup.boolean().required(),
});

type ConfirmKind = TipperAdminConfirmKind | null;

const SITE_STORAGE_KEY = "TippersListPage.siteId";

type TipperEditLocationState = { siteId?: string };

const emptyForm: FormValues = {
    name: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    town: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    carrierLicenceNumber: "",
    licenceExpiryDate: "",
    isEnabled: true,
};

const TipperEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigationSiteId = (location.state as TipperEditLocationState | null)?.siteId ?? "";
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateTippers(roleNames);

    const isNew = !id || id === "new";

    const [siteId, setSiteId] = useState(navigationSiteId);
    const [siteDisplayName, setSiteDisplayName] = useState("");
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const showSiteField = !isNew || sites.length > 1;
    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>(emptyForm);
    const [metaEnabled, setMetaEnabled] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [siteIsEnabled, setSiteIsEnabled] = useState(true);
    const [siteIsDeleted, setSiteIsDeleted] = useState(false);
    const [siteAccessLevel, setSiteAccessLevel] = useState<'readWrite' | 'readOnly' | 'none'>('readWrite');
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const restoreOnCreate = useTipperRestoreOnCreateConflict({
        restoreDeletedWithCreateValues: (payload) => {
            if (!siteId) return Promise.reject("Site is required.");
            return tippersService.restoreDeletedWithCreateValues(siteId, payload);
        },
        onRestored: (detail) =>
            navigate(`/tippers/${detail.id}`, { replace: true, state: { siteId: detail.siteId } }),
    });

    const formAutoFocusRef = useFormAutoFocus(!loading, [loading, id]);

    const readOnlyForm = isSiteScopedFormReadOnly(metaDeleted, siteAccessLevel);
    const lifecycleProps = useMemo(
        () => getEditFormLifecycleProps(readOnlyForm, metaDeleted, siteIsDeleted),
        [readOnlyForm, metaDeleted, siteIsDeleted]
    );

    const headerMenuItem = useMemo(
        () => ({ isEnabled: metaEnabled, isDeleted: metaDeleted }),
        [metaEnabled, metaDeleted]
    );

    const headerMenuActions = useMemo(
        () =>
            filterEditFormLifecycleHeaderActions(
                buildTipperAdminPagedListActions((kind) => setConfirmKind(kind)),
                { readOnlyForm, siteIsDeleted, metaDeleted }
            ),
        [readOnlyForm, siteIsDeleted, metaDeleted]
    );

    const applyTipperDetail = (tipper: ITipperDetail) => {
        setSiteId(tipper.siteId);
        if (typeof localStorage !== "undefined") {
            localStorage.setItem(SITE_STORAGE_KEY, tipper.siteId);
        }
        setSiteDisplayName(tipper.siteName ?? "");
        setInitialValues({
            id: tipper.id,
            name: tipper.name,
            ...hydratePostalAddressForForm(tipper),
            carrierLicenceNumber: nullToEmptyString(tipper.carrierLicenceNumber).toUpperCase(),
            licenceExpiryDate: fromUtcIsoToDateInput(tipper.carrierLicenceExpiryUtc),
            isEnabled: tipper.isEnabled,
        });
        setMetaEnabled(tipper.isEnabled);
        setMetaDeleted(tipper.isDeleted);
        setSiteIsEnabled(tipper.siteIsEnabled);
        setSiteIsDeleted(tipper.siteIsDeleted);
        setSiteAccessLevel(tipper.siteAccessLevel ?? 'readWrite');
        setCreatedAt(tipper.createdAtUtc);
        setUpdatedAt(tipper.updatedAtUtc);
        setCreatedByDisplayName(tipper.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(tipper.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(tipper.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(tipper.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(tipper.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(tipper.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(tipper);
    };

    useEffect(() => {
        if (isNew) {
            setLoading(true);
            sitesService
                .listForOrganisation()
                .then((list) => {
                    const eligibleSites = filterDefaultsEligibleSites(list);
                    setSites(eligibleSites);
                    if (eligibleSites.length === 1) {
                        setSiteId(eligibleSites[0].id);
                    } else if (navigationSiteId && eligibleSites.some((s) => s.id === navigationSiteId)) {
                        setSiteId(navigationSiteId);
                    }
                    setInitialValues(emptyForm);
                })
                .catch((e: unknown) => {
                    logErrorForDev("TipperEditPage.sites", e);
                    setLoadError(getUserFacingErrorMessage(e, "Could not load sites."));
                })
                .finally(() => setLoading(false));
            return;
        }
        if (!id) return;
        setLoading(true);
        setLoadError(null);
        const loadSiteId =
            navigationSiteId ||
            siteId ||
            (typeof localStorage !== "undefined" ? localStorage.getItem(SITE_STORAGE_KEY) : null) ||
            "";
        if (!loadSiteId) {
            setLoadError("Site context is missing. Open this tipper from the tippers list.");
            setLoading(false);
            return;
        }
        tippersService
            .getById(loadSiteId, id)
            .then(applyTipperDetail)
            .catch((e: unknown) => {
                logErrorForDev("TipperEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load tipper."));
            })
            .finally(() => setLoading(false));
    }, [id, isNew, navigationSiteId]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.tipper,
                initialValues.name || `this ${EntityLabels.tipper.toLowerCase()}`
            ),
        [confirmKind, initialValues.name]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || isNew || !kind || !siteId) return;
        const op =
            kind === "disable"
                ? tippersService.disable(siteId, id)
                : kind === "enable"
                  ? tippersService.enable(siteId, id)
                  : kind === "softDelete"
                    ? tippersService.softDelete(siteId, id)
                    : tippersService.restore(siteId, id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getTipperAdminActionSuccessMessage(kind, initialValues.name));
            if (kind === "softDelete") {
                navigate("/tippers", { replace: true });
                return;
            }
            return tippersService.getById(siteId, id).then(applyTipperDetail);
        }).catch((e: unknown) => { setConfirmKind(null); showToastError(getUserFacingErrorMessage(e, DEFAULT_ACTION_FAILED_MESSAGE)); });
    };

    const buildPayload = (values: FormValues): TipperCreatePayload => {
        const t = (s: string) => (typeof s === "string" ? s.trim() : s);
        const address = mapPostalAddressToApi(values);
        return {
            ...address,
            name: t(values.name),
            carrierLicenceNumber: emptyStringToNull(values.carrierLicenceNumber),
            carrierLicenceExpiryUtc: toUtcIsoDateOnly(values.licenceExpiryDate ?? ""),
            isEnabled: values.isEnabled,
        };
    };

    const buildUpdatePayload = (values: FormValues): TipperUpdatePayload => {
        const { isEnabled: _isEnabled, ...createFields } = buildPayload(values);
        return { id: values.id!, ...createFields };
    };

    const buildUpdatePayloadFromDetail = (tipper: ITipperDetail): TipperUpdatePayload =>
        buildUpdatePayload({
            id: tipper.id,
            name: tipper.name,
            ...hydratePostalAddressForForm(tipper),
            carrierLicenceNumber: nullToEmptyString(tipper.carrierLicenceNumber).toUpperCase(),
            licenceExpiryDate: fromUtcIsoToDateInput(tipper.carrierLicenceExpiryUtc),
            isEnabled: tipper.isEnabled,
        });

    const savedUpdatePayloadRef = useRef<TipperUpdatePayload | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage tippers.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.tipper)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/tippers">Back to tippers</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.tipper, isNew)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: SectionLabels.movementParties, to: appRoutes.transportParties },
                    { text: ListPageTitles.tippers, to: "/tippers" },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {editPageTitle(EntityLabels.tipper, isNew)}
                </h1>
                {isNarrowScreen && !isNew && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="tipper-edit-actions-dropdown"
                        ariaLabel="Tipper actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            {!isNew && (
                <RecordAuditMetaBar
                    formatDate={formatTipperMetaDate}
                    formatByLine={formatByLine}
                    createdAt={createdAt}
                    createdByDisplayName={createdByDisplayName}
                    updatedAt={updatedAt}
                    lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                    isDeleted={metaDeleted}
                    isEnabled={metaEnabled}
                    deletedAuditAtUtc={deletedAuditAtUtc}
                    deletedByDisplayName={deletedByDisplayName}
                    disabledAuditAtUtc={disabledAuditAtUtc}
                    disabledByDisplayName={disabledByDisplayName}
                />
            )}

            <ParentEntityLifecycleBanner
                entityLabel={EntityLabels.tipper}
                name={initialValues.name}
                entityIsDeleted={metaDeleted}
                entityIsEnabled={metaEnabled}
                parentSiteName={siteDisplayName}
                parentSiteIsDeleted={siteIsDeleted}
                parentSiteIsEnabled={siteIsEnabled}
                accessLevel={siteAccessLevel}
            />

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { resetForm }) => {
                    if (readOnlyForm) return;
                    if (!siteId) {
                        showToastError("Site is required.");
                        return;
                    }
                    saveOutcomeRef.current = null;
                    setLiveValidationAfterSubmit(true);
                    try {
                        const base = buildPayload(values);
                        if (isNew) {
                            restoreOnCreate.setPendingRestoreForm(base);
                            try {
                                const created = await tippersService.create(siteId, base);
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess(`${formatTipperToastLabel(base.name)} created successfully!`);
                                navigate(`/tippers/${created.id}`, { replace: true, state: { siteId } });
                            } catch (err: unknown) {
                                if (restoreOnCreate.handleCreateError(err)) return;
                                showToastError(
                                    typeof err === "string" ? err : DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE
                                );
                            }
                            return;
                        }
                        const updatePayload = buildUpdatePayload(values);
                        if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "unchanged";
                            showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                            if (leaveAfterSaveRef.current) return;
                            return;
                        }
                        const result = await tippersService.update(siteId, updatePayload);
                        if (isUnchangedUpdateResponse(result)) {
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "unchanged";
                            showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                            if (leaveAfterSaveRef.current) return;
                            return;
                        }
                        setLiveValidationAfterSubmit(false);
                        saveOutcomeRef.current = "success";
                        savedUpdatePayloadRef.current = updatePayload;
                        showToastSuccess(`${formatTipperToastLabel(base.name)} saved successfully!`);
                        resetForm({ values });
                        if (leaveAfterSaveRef.current) return;
                        navigate("/tippers", { replace: true });
                    } catch (err: unknown) {
                        saveOutcomeRef.current = "failure";
                        showToastError(typeof err === "string" ? err : DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ errors, touched, isSubmitting, handleSubmit, validateForm, setTouched, values }) => {
                    const form = (
                    <FormikForm
                        ref={formAutoFocusRef}
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        {metaDeleted && (
                            <Alert variant="warning" className="mb-3">
                                This tipper is deleted. Restore it before you make any changes.
                            </Alert>
                        )}

                        <TipperEditFormGrid
                            showSiteField={showSiteField}
                            isNew={isNew}
                            metaDeleted={readOnlyForm}
                            siteId={siteId}
                            setSiteId={setSiteId}
                            siteDisplayName={siteDisplayName}
                            sites={sites}
                            liveValidationAfterSubmit={liveValidationAfterSubmit}
                            errors={errors}
                            touched={touched}
                        />

                        <EditFormSplitFooter
                            start={
                                shouldShowEditFormLifecycleActions(isNew, values.id, isNarrowScreen) ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        <EditFormLifecycleActions
                                            metaDeleted={metaDeleted}
                                            metaEnabled={metaEnabled}
                                            onAction={setConfirmKind}
                                            {...lifecycleProps}
                                        />
                                    </div>
                                ) : undefined
                            }
                        >
                            <EditFormSaveCancelButtons
                                isSubmitting={isSubmitting}
                                disabled={readOnlyForm}
                                readOnlyForm={readOnlyForm}
                                isNewRecord={isNew}
                                newRecordLabel={EntityLabels.tipper}
                                saveLabel={isNew ? "Create" : "Save"}
                                enableSaveBeforeLeave={!isNew}
                                leaveAfterSaveRef={leaveAfterSaveRef}
                                saveOutcomeRef={saveOutcomeRef}
                                onCancel={() => navigate("/tippers")}
                            />
                        </EditFormSplitFooter>
                    </FormikForm>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            readOnlyForm={readOnlyForm}
                            isNewRecord={isNew}
                            newRecordLabel={EntityLabels.tipper}
                            enableSaveBeforeLeave={!isNew}
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={restoreOnCreate.restoreModalProps.show}
                onClose={(confirmed) => void restoreOnCreate.confirmRestore(confirmed)}
                title={restoreOnCreate.restoreModalProps.title}
                body={restoreOnCreate.restoreModalProps.body}
                buttonSuccessText={restoreOnCreate.restoreModalProps.buttonSuccessText}
                buttonCancelText={restoreOnCreate.restoreModalProps.buttonCancelText}
            />
        </Container>
    );
};

export { TipperEditPage };
